import { useEffect, useMemo, useState } from "react";
import { api } from "@/lib/api";
import { Input } from "@/components/ui/input";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ListMagnifyingGlass, ArrowClockwise } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";

const ACTION_TONE = {
  "user.create": "bg-emerald-50 text-emerald-800 border-emerald-200",
  "user.delete": "bg-rose-50 text-rose-800 border-rose-200",
  "user.update": "bg-blue-50 text-blue-800 border-blue-200",
  "user.role_change": "bg-purple-50 text-purple-800 border-purple-200",
  "user.password_reset_link": "bg-amber-50 text-amber-800 border-amber-200",
  "user.password_reset": "bg-amber-50 text-amber-800 border-amber-200",
  archive: "bg-rose-50 text-rose-800 border-rose-200",
  restore: "bg-emerald-50 text-emerald-800 border-emerald-200",
  "admin.reset_database": "bg-rose-50 text-rose-800 border-rose-200",
};

const ACTIONS = [
  { value: "all", label: "All actions" },
  { value: "user.create", label: "User created" },
  { value: "user.update", label: "User updated" },
  { value: "user.role_change", label: "Role changed" },
  { value: "user.delete", label: "User deleted" },
  { value: "user.password_reset_link", label: "Password reset link sent" },
  { value: "user.password_reset", label: "Password changed" },
  { value: "archive", label: "Archive (soft-delete)" },
  { value: "restore", label: "Restore from archive" },
  { value: "admin.reset_database", label: "Database reset" },
  { value: "approval.budget.in_progress", label: "Budget approved" },
  { value: "approval.budget.rejected", label: "Budget rejected" },
  { value: "approval.billing.approve", label: "Billing approved (email)" },
  { value: "approval.billing.reject", label: "Billing rejected (email)" },
];

export default function AuditLogPage() {
  const [logs, setLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [action, setAction] = useState("all");
  const [query, setQuery] = useState("");

  async function load() {
    setLoading(true);
    try {
      const params = {};
      if (action !== "all") params.action = action;
      const { data } = await api.get("/audit-logs", { params });
      setLogs(data || []);
    } finally { setLoading(false); }
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [action]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return logs;
    return logs.filter((l) =>
      [l.actor_name, l.action, l.target_type, l.target_id, l.target_label, JSON.stringify(l.meta || {})]
        .join(" ")
        .toLowerCase()
        .includes(q)
    );
  }, [logs, query]);

  return (
    <div className="space-y-6" data-testid="audit-log-page">
      <div className="flex items-end justify-between border-b border-border pb-6">
        <div>
          <div className="section-caption mb-2 flex items-center gap-1.5"><ListMagnifyingGlass size={12} /> Compliance</div>
          <h1 className="section-title text-3xl sm:text-4xl">Audit Log</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Every sensitive action across the system: user changes, archives, restores, approvals, and admin operations. Newest first.
          </p>
        </div>
        <Button variant="outline" className="border-border" onClick={load} data-testid="audit-refresh-btn">
          <ArrowClockwise size={14} className="mr-2" /> Refresh
        </Button>
      </div>

      <div className="flex gap-3">
        <div className="flex-1">
          <Input value={query} onChange={(e) => setQuery(e.target.value)} placeholder="Search actor, target, or details…" className="border-border" data-testid="audit-search-input" />
        </div>
        <div className="w-64">
          <Select value={action} onValueChange={setAction}>
            <SelectTrigger data-testid="audit-action-filter"><SelectValue /></SelectTrigger>
            <SelectContent>
              {ACTIONS.map((a) => <SelectItem key={a.value} value={a.value}>{a.label}</SelectItem>)}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="border border-border rounded-md bg-card overflow-x-auto">
        <Table className="min-w-[900px]">
          <TableHeader>
            <TableRow>
              <TableHead className="w-[170px]">When</TableHead>
              <TableHead>Actor</TableHead>
              <TableHead>Action</TableHead>
              <TableHead>Target</TableHead>
              <TableHead>Details</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground">Loading…</TableCell></TableRow>
            ) : filtered.length === 0 ? (
              <TableRow><TableCell colSpan={5} className="py-10 text-center text-sm text-muted-foreground">No audit entries match.</TableCell></TableRow>
            ) : filtered.map((l) => {
              const tone = ACTION_TONE[l.action] || (l.action?.startsWith("approval") ? "bg-blue-50 text-blue-800 border-blue-200" : "bg-muted text-foreground border-border");
              return (
                <TableRow key={l.id} data-testid={`audit-row-${l.id}`}>
                  <TableCell className="text-xs text-muted-foreground tabular-nums">
                    {(l.created_at || "").slice(0, 19).replace("T", " ")}
                  </TableCell>
                  <TableCell>
                    <div className="text-sm font-medium">{l.actor_name || "system"}</div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{l.actor_role || ""}</div>
                  </TableCell>
                  <TableCell>
                    <span className={`text-[10px] uppercase tracking-widest px-2 py-0.5 rounded border ${tone}`}>{l.action}</span>
                  </TableCell>
                  <TableCell className="text-sm">
                    <div className="font-medium">{l.target_label || "—"}</div>
                    <div className="text-[10px] uppercase tracking-widest text-muted-foreground">{l.target_type}</div>
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground max-w-md">
                    <div className="truncate" title={JSON.stringify(l.meta || {})}>
                      {l.meta && Object.keys(l.meta).length > 0 ? Object.entries(l.meta).map(([k, v]) => `${k}: ${typeof v === "object" ? JSON.stringify(v) : v}`).join(" · ") : "—"}
                    </div>
                  </TableCell>
                </TableRow>
              );
            })}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}
